import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Card, Text } from '@teals/ui-web';
import { useAuth } from '../../hooks';
import {
  useGetApplicationsQuery,
  useGetOrganisationQuery,
  useSubmitOauthConsentMutation,
  type McpScope,
} from '../../store/api';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 3rem 1.25rem;
  background: var(--bg-canvas, #f8fafc);
`;

const Panel = styled(Card)`
  width: 100%;
  max-width: 520px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ScopeRow = styled.label`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  font-size: 0.875rem;
`;

const ALL_SCOPES: { id: McpScope; label: string; hint: string }[] = [
  { id: 'agents:invoke', label: 'Invoke', hint: 'List, get, and run agents as you' },
  { id: 'agents:write', label: 'Write', hint: 'Create and edit drafts (opt-in)' },
  { id: 'agents:publish', label: 'Publish', hint: 'Publish a draft with confirm (opt-in)' },
];

const OauthConsent: React.FC = () => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const orgId = user?.organizationId || '';
  const { data: organisation } = useGetOrganisationQuery(orgId, { skip: !orgId });
  const { data: apps = [] } = useGetApplicationsQuery(
    { orgId },
    { skip: !orgId },
  );
  const [submitConsent, { isLoading }] = useSubmitOauthConsentMutation();

  const clientId = params.get('client_id') || 'cursor';
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || 'S256';
  const scopeParam = params.get('scope') || '';

  const initialScopes = useMemo(() => {
    const fromQuery = scopeParam
      .split(/[,\s]+/)
      .filter((item): item is McpScope =>
        ALL_SCOPES.some((scope) => scope.id === item),
      );
    return fromQuery.length ? fromQuery : (['agents:invoke'] as McpScope[]);
  }, [scopeParam]);

  const [scopes, setScopes] = useState<McpScope[]>(initialScopes);
  const [appIds, setAppIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const toggleScope = (scope: McpScope) => {
    if (scope === 'agents:invoke') return;
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope],
    );
  };

  const toggleApp = (appId: string) => {
    setAppIds((current) =>
      current.includes(appId)
        ? current.filter((item) => item !== appId)
        : [...current, appId],
    );
  };

  const handleConnect = async () => {
    setError(null);
    if (!orgId) {
      setError('Choose an organisation before connecting.');
      return;
    }
    if (!redirectUri || !codeChallenge) {
      setError('This consent link is missing OAuth parameters. Restart Connect from Cursor.');
      return;
    }
    try {
      const result = await submitConsent({
        orgId,
        clientId,
        redirectUri,
        state,
        codeChallenge,
        codeChallengeMethod,
        capabilities: scopes,
        appIds,
        name: 'Cursor',
      }).unwrap();
      if (result.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }
      setError('Consent succeeded but no redirect URL was returned.');
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message || 'Consent failed')
          : 'Consent failed';
      setError(message);
    }
  };

  return (
    <Page>
      <Panel>
        <Text variant="h2">Connect Cursor</Text>
        <Text variant="body2" style={{ color: 'var(--text-muted)' }}>
          {clientId} wants to call Luminafactory MCP as <strong>{user?.email || 'you'}</strong>{' '}
          in this organisation. The grant is bound here — later MCP calls do not follow a
          later organisation switch in Business Center.
        </Text>

        <label>
          <Text variant="h6">Organisation</Text>
          <select
            aria-label="Organisation"
            value={orgId}
            disabled
            style={{ width: '100%', marginTop: '0.35rem', padding: '0.5rem' }}
          >
            <option value={orgId}>{organisation?.name || orgId || 'No organisation'}</option>
          </select>
        </label>

        {ALL_SCOPES.map((scope) => (
          <ScopeRow key={scope.id}>
            <input
              type="checkbox"
              checked={scopes.includes(scope.id)}
              disabled={scope.id === 'agents:invoke'}
              onChange={() => toggleScope(scope.id)}
            />
            <span>
              <strong>{scope.label}</strong> — {scope.hint}
            </span>
          </ScopeRow>
        ))}

        <div>
          <Text variant="h6">Applications (optional)</Text>
          <Text variant="caption" style={{ color: 'var(--text-muted)' }}>
            Leave empty for the playground-style catalog. Selecting apps intersects the catalog.
          </Text>
          {apps.length === 0 ? (
            <Text variant="body2" style={{ marginTop: '0.5rem' }}>
              No applications yet. You can still Connect, or create one in{' '}
              <Link to="/applications">Applications</Link>.
            </Text>
          ) : (
            apps.map((app) => (
              <ScopeRow key={app.appId}>
                <input
                  type="checkbox"
                  checked={appIds.includes(app.appId)}
                  onChange={() => toggleApp(app.appId)}
                />
                <span>{app.name}</span>
              </ScopeRow>
            ))
          )}
        </div>

        {error ? (
          <Text variant="body2" style={{ color: 'var(--danger, #dc2626)' }}>
            {error}
          </Text>
        ) : null}

        <Button onClick={handleConnect} disabled={isLoading || !orgId}>
          Connect
        </Button>
        <Text variant="caption" style={{ color: 'var(--text-muted)' }}>
          Redirects back to Cursor with an authorization code. Cognito tokens stay in this
          browser.
        </Text>
      </Panel>
    </Page>
  );
};

export default OauthConsent;
